/**
 * BlockNote Autosave Controller
 *
 * Universal Stimulus controller for automatic saving of BlockNote content.
 * Works with any resource that has an autosave endpoint. Provides debounced
 * save functionality with visual feedback.
 *
 * Features:
 * - Debounced autosave (configurable, default 500ms)
 * - Save status indicators (Saving..., Saved, Save failed)
 * - Unsaved changes warning on page navigation
 * - Graceful handling of new resources without autosave URLs
 * - CSRF token support
 *
 * Usage:
 *   <div data-controller="blocks-text-editor blocks-text-autosave"
 *        data-blocks-text-autosave-autosave-url-value="<%= autosave_document_path(@document) %>"
 *        data-blocks-text-autosave-debounce-ms-value="500"
 *        data-blocks-text-autosave-resource-name-value="document"
 *        data-action="blocks-text-editor:contentChanged->blocks-text-autosave#contentChanged">
 *     <span data-blocks-text-autosave-target="saveIndicator"></span>
 *   </div>
 *
 * Targets:
 *   saveIndicator  - Element to display save status messages
 *
 * Values:
 *   autosaveUrl    - URL endpoint for PATCH request (empty string for new resources)
 *   debounceMs     - Debounce delay in milliseconds (default: 500)
 *   resourceName   - Resource name for logging (default: "resource")
 *
 * Events Listened:
 *   blocks-text-editor:contentChanged - Triggered by editor controller on content change
 *
 * API Contract:
 *   The autosave endpoint should:
 *   - Accept PATCH requests
 *   - Expect FormData with "content" field containing JSON string
 *   - Return JSON response with success/error status
 *   - Example: PATCH /documents/:id/autosave
 *
 * Related Files:
 * - app/components/ui/blocks_text_editor_component.rb
 * - app/javascript/controllers/blocks_text_editor_controller.js
 * - app/controllers/documents_controller.rb (autosave action)
 * - app/controllers/admin/pages_controller.rb (autosave action)
 *
 * Dependencies:
 * - @hotwired/stimulus
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["saveIndicator"];
  static values = {
    autosaveUrl: String,
    debounceMs: { type: Number, default: 500 },
    resourceName: { type: String, default: "resource" },
  };

  /**
   * Lifecycle: Called when controller connects to the DOM
   * Initializes autosave state and sets up beforeunload warning
   */
  connect() {
    console.log("BlocksText autosave controller connected");
    this.autosaveTimer = null;
    this.hasUnsavedChanges = false;
    this.setupBeforeUnloadWarning();
  }

  /**
   * Lifecycle: Called when controller disconnects from the DOM
   * Cleans up timer and event listeners
   */
  disconnect() {
    if (this.autosaveTimer) {
      clearTimeout(this.autosaveTimer);
    }
    if (this.saveIndicatorHideTimeout) {
      clearTimeout(this.saveIndicatorHideTimeout);
    }
    this.removeBeforeUnloadWarning();
  }

  /**
   * Event handler for content changes from editor controller
   *
   * Marks content as unsaved and schedules an autosave operation.
   * Called by blocks-text-editor controller via custom event.
   *
   * @param {CustomEvent} event - Contains { detail: { content: jsonString } }
   */
  contentChanged(event) {
    const content = event.detail.content;
    this.hasUnsavedChanges = true;
    this.scheduleAutosave(content);
  }

  /**
   * Schedules an autosave operation with debouncing
   *
   * Clears any existing timer and schedules a new save after the
   * configured debounce delay. Shows "Saving..." indicator immediately.
   *
   * @param {String} content - JSON string of BlockNote content
   */
  scheduleAutosave(content) {
    // Clear existing timer
    if (this.autosaveTimer) {
      clearTimeout(this.autosaveTimer);
    }

    // Show "Saving..." indicator
    this.showSaveIndicator("Saving...", "saving");

    // Schedule new autosave with debounce
    this.autosaveTimer = setTimeout(() => {
      this.performAutosave(content);
    }, this.debounceMsValue);
  }

  /**
   * Performs the actual autosave HTTP request
   *
   * Sends a PATCH request to the autosave endpoint with content.
   * Handles success/failure states and updates indicators.
   * Gracefully skips save for new resources without URLs.
   *
   * @async
   * @param {String} content - JSON string of BlockNote content
   */
  async performAutosave(content) {
    // Skip autosave if no URL provided or URL is empty (new resources)
    if (
      !this.hasAutosaveUrlValue ||
      !this.autosaveUrlValue ||
      this.autosaveUrlValue.trim() === ""
    ) {
      console.log(
        `Autosave skipped: no URL provided (new ${this.resourceNameValue})`,
      );
      this.showSaveIndicator("\u00A0", "success"); // Clear the indicator with placeholder
      return;
    }

    try {
      const formData = new FormData();
      formData.append("content", content);

      const csrf = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch(this.autosaveUrlValue, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: formData,
      });

      if (response.ok) {
        this.hasUnsavedChanges = false;
        this.showSaveIndicator("Saved", "success");
      } else {
        const data = await response.json().catch(() => ({}));
        console.error("Autosave failed:", data);
        this.showSaveIndicator("Save failed", "error");
      }
    } catch (error) {
      console.error("Autosave error:", error);
      this.showSaveIndicator("Save failed", "error");
    }
  }

  /**
   * Shows a save status indicator message
   *
   * @param {String} message - Message to display
   * @param {String} type - Message type: "saving", "success", or "error"
   */
  showSaveIndicator(message, type = "success") {
    if (!this.hasSaveIndicatorTarget) return;

    // Clear any pending auto-hide timeout to prevent race conditions
    if (this.saveIndicatorHideTimeout) {
      clearTimeout(this.saveIndicatorHideTimeout);
      this.saveIndicatorHideTimeout = null;
    }

    const indicator = this.saveIndicatorTarget;
    indicator.textContent = message || "\u00A0";

    // Remove state classes
    indicator.classList.remove("badge-success", "badge-error", "opacity-0", "opacity-100");

    // Add appropriate classes
    if (type === "saving") {
      indicator.classList.add("opacity-100");
    } else if (type === "success") {
      indicator.classList.add("badge-success", "opacity-100");
    } else if (type === "error") {
      indicator.classList.add("badge-error", "opacity-100");
    }

    // Auto-hide after 2 seconds for success/error
    if (type === "success" || type === "error") {
      this.saveIndicatorHideTimeout = setTimeout(() => {
        indicator.textContent = "\u00A0";
        indicator.classList.remove("badge-success", "badge-error", "opacity-100");
        indicator.classList.add("opacity-0");
      }, 2000);
    }
  }

  /**
   * Sets up browser warning for unsaved changes
   *
   * Shows a confirmation dialog if user tries to navigate away
   * with unsaved changes.
   */
  setupBeforeUnloadWarning() {
    this.beforeUnloadHandler = (event) => {
      if (this.hasUnsavedChanges) {
        const warning =
          "You have unsaved changes. Are you sure you want to leave?";
        event.preventDefault();
        event.returnValue = warning;
        return warning;
      }
    };
    window.addEventListener("beforeunload", this.beforeUnloadHandler);
  }

  /**
   * Removes the beforeunload warning event listener
   *
   * Called during controller disconnect to clean up
   */
  removeBeforeUnloadWarning() {
    if (this.beforeUnloadHandler) {
      window.removeEventListener("beforeunload", this.beforeUnloadHandler);
    }
  }
}
